import { deliveryTimeArray, showDate2, showErrorTost, showSuccessTost } from 'src/constants';
import { Box, Button, Grid, Typography } from '@mui/material';
import { manualOrderSchema, newOrderSchema } from 'src/config/yup';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { CREATE_ORDER } from 'src/apiService/apiDeclaration';
import CustomSelectField from '../formFields/selectField';
import LoaderButton from '../loaderButton/loaderButton';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomTextField from '../formFields/textField';
import { ICONS } from 'src/assets/library';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { t } from 'i18next';

const NewOrderForm = ({ onSuccess }) => {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [subOrder, setSubOrder] = useState([]);
  const [orderData, setOrderData] = useState(null);
  const isAdminOrder = user.role !== 'CUSTOMER';
  const schema = isAdminOrder ? manualOrderSchema : newOrderSchema;

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: isAdminOrder
      ? { role: 'CUSTOMER', timeOfDelivery: 'default' }
      : {
          customerId: user._id,
          timeOfDelivery: 'default',
          streetAddressOrder: user.streetAddress,
          streetNumberOrder: user.streetNumber,
          municipalityOrder: user.municipality,
          cityOrder: user.city,
          email: user.email,
        },
  });

  const removeFieldsAndStore = (body) => {
    const {
      volumeOfConcrete,
      dosage,
      deliveryDate,
      timeOfDelivery,
      streetAddressOrder,
      streetNumberOrder,
      municipalityOrder,
      cityOrder,
      ...customerData
    } = body;
    const order = {
      volumeOfConcrete,
      dosage,
      deliveryDate,
      timeOfDelivery,
      streetAddressOrder,
      streetNumberOrder,
      municipalityOrder,
      cityOrder,
    };
    return { customerData, order };
  };
  const currentValues = getValues();
  const addNewOrder = (body) => {
    const { customerData, order } = removeFieldsAndStore(body);
    if (orderData === null) {
      setOrderData(customerData);
    }
    setSubOrder([...subOrder, { ...order, _id: subOrder.length }]);

    reset({
      ...currentValues,
      volumeOfConcrete: '',
      dosage: '',
      deliveryDate: '',
      timeOfDelivery: '',
      streetAddressOrder: currentValues.streetAddress && currentValues.streetAddress,
      streetNumberOrder: currentValues.streetNumber && currentValues.streetNumber,
      municipalityOrder: currentValues.municipality && currentValues.municipality,
      cityOrder: currentValues.city && currentValues.city,
    });
  };

  const removeOrderSuffix = (array) => {
    return array.map((item) => {
      const { _id, ...rest } = item;

      // Replace keys ending with 'Order'
      const newItem = Object.entries(rest).reduce((acc, [key, value]) => {
        const newKey = key.endsWith('Order') ? key.replace(/Order$/, '') : key;
        acc[newKey] = value;
        return acc;
      }, {});

      return newItem;
    });
  };

  const confirmNewOrder = async () => {
    setLoading(true);
    const validkeysSubOrder = removeOrderSuffix(subOrder);
    const body = { ...orderData, subOrder: validkeysSubOrder };
    try {
      let resp = await CREATE_ORDER(body);
      showSuccessTost(resp.message);
      onSuccess();
    } catch (error) {
      showErrorTost(error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteOrder = (id) => {
    const orderToBeDeleted = subOrder.findIndex((sub) => sub._id === id);
    const updatedSubOrders = [...subOrder.slice(0, orderToBeDeleted), ...subOrder.slice(orderToBeDeleted + 1)];
    setSubOrder(updatedSubOrders);
  };
  const editOrder = (order) => {
    const currentValues = getValues();
    reset({
      ...currentValues,
      volumeOfConcrete: order.volumeOfConcrete,
      dosage: order.dosage,
      deliveryDate: showDate2(order.deliveryDate),
      timeOfDelivery: order.timeOfDelivery,
      streetAddressOrder: order.streetAddressOrder,
      streetNumberOrder: order.streetNumberOrder,
      municipalityOrder: order.municipalityOrder,
      cityOrder: order.cityOrder,
    });
    deleteOrder(order._id);
  };
  return (
    <>
      <form onSubmit={handleSubmit(addNewOrder)}>
        <Grid container spacing={2}>
          {isAdminOrder && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6">{t('orderDetailsPage.customerDetails')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label={'Email'}
                  type="email"
                  placeholder={t('common.placeholderEmail')}
                  controller={{
                    name: 'email',
                    control: control,
                    errors: errors.email,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label={t('common.phone')}
                  type="text"
                  placeholder={'+243811234567'}
                  controller={{
                    name: 'phoneNumber',
                    control: control,
                    errors: errors.phoneNumber,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label={t('common.fName')}
                  placeholder={t('common.placeholderFirstName')}
                  type="name"
                  controller={{
                    name: 'firstName',
                    control: control,
                    errors: errors.firstName,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label={t('common.lName')}
                  placeholder={t('common.placeholderLastName')}
                  type="name"
                  controller={{
                    name: 'lastName',
                    control: control,
                    errors: errors.lastName,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label={t('common.stAddress')}
                  type="name"
                  controller={{
                    name: 'streetAddress',
                    control: control,
                    errors: errors.streetAddress,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label={t('common.stNo')}
                  type="name"
                  controller={{
                    name: 'streetNumber',
                    control: control,
                    errors: errors.streetNumber,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label={t('common.municipality')}
                  type="name"
                  controller={{
                    name: 'municipality',
                    control: control,
                    errors: errors.municipality,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label={t('common.city')}
                  type="name"
                  controller={{
                    name: 'city',
                    control: control,
                    errors: errors.city,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label={t('signupPage.reference')}
                  type="name"
                  controller={{
                    name: 'reference',
                    control: control,
                    errors: errors.reference,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">{t('orderDetailsPage.subOrderDetails')}</Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('customerPage.volumeOfConcrete')}
              type="number"
              controller={{
                name: 'volumeOfConcrete',
                control: control,
                errors: errors.volumeOfConcrete,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('customerPage.dosage')}
              controller={{
                name: 'dosage',
                control: control,
                errors: errors.dosage,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t('revenue.endDate')}
                format="DD/MM/YYYY"
                renderInput={(params) => <input {...params} />}
              />
            </LocalizationProvider> */}
            <CustomTextField
              label={t('customerPage.deliveryDate')}
              type="date"
              controller={{
                name: 'deliveryDate',
                control: control,
                errors: errors.deliveryDate,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomSelectField
              label={t('customerPage.deliveryTime')}
              optionsArray={deliveryTimeArray}
              controller={{
                name: 'timeOfDelivery',
                control: control,
                errors: errors.timeOfDelivery,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('common.stAddress')}
              type="name"
              controller={{
                name: 'streetAddressOrder',
                control: control,
                errors: errors.streetAddressOrder,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('common.stNo')}
              type="name"
              controller={{
                name: 'streetNumberOrder',
                control: control,
                errors: errors.streetNumberOrder,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('common.municipality')}
              type="name"
              controller={{
                name: 'municipalityOrder',
                control: control,
                errors: errors.municipalityOrder,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              label={t('common.city')}
              type="name"
              controller={{
                name: 'cityOrder',
                control: control,
                errors: errors.cityOrder,
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginY: 2 }}
          >
            <Grid item xs={12} sm={5.8}>
              <LoaderButton buttonText={t('customerPage.addToOrder')} type="submit" disabled={!isValid} />
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ width: '2rem', height: '2rem', position: 'relative' }}>
                <ShoppingCartIcon sx={{ height: '100%', width: '100%' }} />
                <Box
                  sx={{
                    width: '1.25rem',
                    height: '1.25rem',
                    borderRadius: '50%',
                    position: 'absolute',
                    zIndex: '20',
                    top: '-36%',
                    right: '-4%',
                    bgcolor: '#acd629',
                    border: '1px solid white',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {subOrder.length}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        {/* <Container> */}
        {subOrder.length > 0 && (
          <Grid item sx={{ paddingTop: 2 }}>
            <Box
              sx={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.9rem',
                overflowX: 'auto', // Enable horizontal scrolling
              }}
            >
              <Box sx={{ minWidth: { md: '350px', xs: '400px' } }}>
                <Grid
                  container
                  sx={{
                    borderBottom: '1px solid #ddd',
                    textAlign: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Grid item sx={{ borderRight: '1px solid #ddd', width: '10%' }}>
                    <Typography variant="h8">{t('common.serialNo')}</Typography>
                  </Grid>
                  <Grid item sx={{ borderRight: '1px solid #ddd', width: '15%' }}>
                    <Typography variant="h8">{t('common.volume')}</Typography>
                  </Grid>
                  <Grid item sx={{ borderRight: '1px solid #ddd', width: '15%' }}>
                    <Typography variant="h8">{t('customerPage.dosage')}</Typography>
                  </Grid>
                  <Grid item sx={{ borderRight: '1px solid #ddd', width: '20%' }}>
                    <Typography variant="h8">{t('customerPage.deliveryDate')}</Typography>
                  </Grid>
                  <Grid item sx={{ borderRight: '1px solid #ddd', width: '20%' }}>
                    <Typography variant="h8">{t('customerPage.deliveryTime')}</Typography>
                  </Grid>
                  <Grid item sx={{ width: '9%' }}></Grid>
                  <Grid item sx={{ width: '9%' }}></Grid>
                </Grid>
                {/* Table Rows */}
                {subOrder.map((item, index) => (
                  <Grid
                    container
                    key={index}
                    sx={{
                      borderBottom: '1px solid #ddd',
                      textAlign: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Grid item sx={{ borderRight: '1px solid #ddd', paddingY: '0.5rem', width: '10%' }}>
                      <Typography sx={{ fontSize: '0.9rem' }}>{index + 1}</Typography>
                    </Grid>
                    <Grid item sx={{ borderRight: '1px solid #ddd', paddingY: '0.5rem', width: '15%' }}>
                      <Typography sx={{ fontSize: '0.9rem' }}>{item.volumeOfConcrete}</Typography>
                    </Grid>
                    <Grid item sx={{ borderRight: '1px solid #ddd', paddingY: '0.5rem', width: '15%' }}>
                      <Typography sx={{ fontSize: '0.9rem' }}>{item.dosage}</Typography>
                    </Grid>
                    <Grid item sx={{ borderRight: '1px solid #ddd', paddingY: '0.5rem', width: '20%' }}>
                      <Typography sx={{ fontSize: '0.9rem' }}>{showDate2(item.deliveryDate)}</Typography>
                    </Grid>
                    <Grid item sx={{ borderRight: '1px solid #ddd', paddingY: '0.5rem', width: '20%' }}>
                      <Typography sx={{ fontSize: '0.9rem' }}>{t(`labels.${item.timeOfDelivery}`)}</Typography>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        borderRight: '1px solid #ddd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '9%',
                      }}
                    >
                      <Button sx={{ fontSize: '0.6rem', color: 'red' }} onClick={() => deleteOrder(item._id)}>
                        {ICONS.delete}
                      </Button>
                    </Grid>
                    <Grid
                      item
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '9%',
                      }}
                    >
                      <Button sx={{ fontSize: '0.6rem' }} onClick={() => editOrder(item)}>
                        {ICONS.edit}
                      </Button>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            </Box>
          </Grid>
        )}
        {/* </Container> */}
        <Grid container xs={12} sx={{ justifyContent: 'flex-end', marginY: 2 }}>
          <Grid item xs={12} sm={6}>
            <LoaderButton
              buttonText={t('common.confirmOrder')}
              disabled={subOrder.length === 0}
              loading={loading}
              onClick={confirmNewOrder}
            />
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default NewOrderForm;
